import { useEffect, useState, type ReactElement } from 'react';
import { Loader2, CheckCircle2, AlertCircle, X, Sparkles, Utensils } from 'lucide-react';
import { useJobs, type Job, type JobType } from '@/lib/jobs';

/**
 * Indicador flotante de jobs IA en curso (M2.27). Se monta en AppLayout
 * y persiste a través de cualquier navegación. Cada job es una pill:
 *
 *   [icono]  Coach IA · semana del 1 jun
 *            1m 23s · pensando…                                 [×]
 *
 * - `running`  → icono Loader2 girando, color teal, tiempo en vivo.
 * - `done`     → CheckCircle2 verde + "Listo · Ver →" (link a resultPath).
 *                Auto-dismiss tras 30s.
 * - `error`    → AlertCircle rojo + mensaje + manual dismiss.
 *
 * Posicionado fixed bottom-right. Apilable si hay >1 job.
 */
export function JobsBanner(): ReactElement | null {
  const { jobs } = useJobs();
  if (jobs.length === 0) return null;

  return (
    <aside
      aria-label="Generaciones en curso"
      aria-live="polite"
      data-testid="jobs-banner"
      className="pointer-events-none fixed bottom-4 right-4 z-50 flex w-full max-w-sm flex-col gap-2"
    >
      {jobs.map((job) => (
        <JobPill key={job.id} job={job} />
      ))}
    </aside>
  );
}

const JOB_ICONS: Record<JobType, typeof Sparkles> = {
  'coach-plan': Sparkles,
  'meal-plan': Utensils,
};

function JobPill({ job }: { job: Job }): ReactElement {
  const { dismissJob } = useJobs();
  const TypeIcon = JOB_ICONS[job.type] ?? Sparkles;

  // Reloj en vivo para `running` — refresca cada segundo el span de tiempo.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (job.status !== 'running') return;
    const t = window.setInterval(() => setTick((n) => n + 1), 1000);
    return () => window.clearInterval(t);
  }, [job.status]);

  const elapsedMs =
    (job.status === 'running' ? Date.now() : job.finishedAt ?? Date.now()) -
    job.startedAt;

  const palette = paletteFor(job.status);

  return (
    <article
      role="status"
      data-testid={`jobs-pill-${job.id}`}
      data-status={job.status}
      className={`pointer-events-auto flex items-start gap-3 rounded-xl border ${palette.border} ${palette.bg} px-3 py-2.5 shadow-lg`}
    >
      <span className={`mt-0.5 shrink-0 ${palette.iconColor}`} aria-hidden="true">
        {job.status === 'running' && <Loader2 className="h-5 w-5 animate-spin" />}
        {job.status === 'done' && <CheckCircle2 className="h-5 w-5" />}
        {job.status === 'error' && <AlertCircle className="h-5 w-5" />}
      </span>
      <div className="min-w-0 flex-1">
        <p className={`flex items-center gap-1.5 text-sm font-medium ${palette.title}`}>
          <TypeIcon aria-hidden="true" className="h-3.5 w-3.5" />
          <span className="truncate">{job.label}</span>
        </p>
        <p className={`mt-0.5 text-xs ${palette.subtitle}`}>
          {job.status === 'running' && (
            <>{formatElapsed(elapsedMs)} · pensando…</>
          )}
          {job.status === 'done' && (
            <>
              Listo en {formatElapsed(elapsedMs)}
              {job.resultPath && (
                <>
                  {' · '}
                  {/* Usamos <a> nativo (no <Link>) porque algunos
                      resultPath llevan query string (ej.
                      `/entrenos?view=week&week=2026-06-01` desde el coach
                      M2.28) y el Link code-based de TanStack requiere
                      declarar searchSchemas. Una navegación full-reload
                      es aceptable aquí: ocurre 1 vez al terminar el
                      job. El JobsProvider se reconstruye y el job ya
                      está dismissed. */}
                  <a
                    href={job.resultPath}
                    className={`font-semibold underline ${palette.linkColor}`}
                    onClick={() => dismissJob(job.id)}
                  >
                    Ver →
                  </a>
                </>
              )}
            </>
          )}
          {job.status === 'error' && (
            <>{job.errorMessage ?? 'Error al generar'}</>
          )}
        </p>
      </div>
      <button
        type="button"
        onClick={() => dismissJob(job.id)}
        aria-label="Cerrar"
        data-testid={`jobs-pill-close-${job.id}`}
        className={`-mr-1 -mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded ${palette.closeHover}`}
      >
        <X className="h-4 w-4" />
      </button>
    </article>
  );
}

interface Palette {
  bg: string;
  border: string;
  title: string;
  subtitle: string;
  iconColor: string;
  linkColor: string;
  closeHover: string;
}

function paletteFor(status: Job['status']): Palette {
  if (status === 'done') {
    return {
      bg: 'bg-emerald-50',
      border: 'border-emerald-300',
      title: 'text-emerald-900',
      subtitle: 'text-emerald-800',
      iconColor: 'text-emerald-700',
      linkColor: 'text-emerald-900',
      closeHover: 'text-emerald-700 hover:bg-emerald-100',
    };
  }
  if (status === 'error') {
    return {
      bg: 'bg-rose-50',
      border: 'border-rose-300',
      title: 'text-rose-900',
      subtitle: 'text-rose-800',
      iconColor: 'text-rose-700',
      linkColor: 'text-rose-900',
      closeHover: 'text-rose-700 hover:bg-rose-100',
    };
  }
  return {
    bg: 'bg-teal-50',
    border: 'border-teal-300',
    title: 'text-teal-900',
    subtitle: 'text-teal-800',
    iconColor: 'text-teal-700',
    linkColor: 'text-teal-900',
    closeHover: 'text-teal-700 hover:bg-teal-100',
  };
}

function formatElapsed(ms: number): string {
  const totalS = Math.floor(ms / 1000);
  const m = Math.floor(totalS / 60);
  const s = totalS % 60;
  if (m === 0) return `${s}s`;
  return `${m}m ${String(s).padStart(2, '0')}s`;
}
